import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Hand, Clock, ClipboardCheck, CalendarDays, AlertTriangle, ArrowRight } from "lucide-react";
import { http } from "../../lib/api";
import { fmtTL, fmtDateTime } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { LIMIT_RISK_ROLES } from "../../lib/roles";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 6) return "İyi geceler";
  if (h < 12) return "Günaydın";
  if (h < 18) return "İyi günler";
  return "İyi akşamlar";
};

function Chip({ to, tone, icon: Icon, label, value, testid }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
    red: "border-red-200 bg-red-50 text-red-800 hover:bg-red-100",
    slate: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
  };
  return (
    <Link to={to} data-testid={testid} className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${tones[tone]}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <span>{label}</span>
      {value != null && <span className="font-mono">{value}</span>}
      <ArrowRight className="w-3.5 h-3.5 opacity-60" />
    </Link>
  );
}

// Dashboard karşılama: ad + önceki giriş + yıl filtresi (ciro / proje kartları) + onay bekleyen kayıt / 7 günlük taksit özeti — yalnız yönetici, patron, finans, muhasebe görür
export default function WelcomeCard({ year, years, onYear }) {
  const { user } = useAuth();
  const allowed = LIMIT_RISK_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  useEffect(() => { if (allowed) http.get("/dashboard/welcome").then((r) => setData(r.data)).catch(() => setData(false)); }, [allowed]);
  if (!allowed || !data) return null;
  const inst = data.installments;
  const quiet = !data.pending_approvals && (!inst || (!inst.due_count && !inst.late_count));
  const now = new Date().getFullYear();
  const yearList = years?.length ? years : [now - 2, now - 1, now, now + 1];
  const curYear = year || now;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-900 text-white px-5 py-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="welcome-card">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center shrink-0"><Hand className="w-5 h-5" /></div>
        <div className="min-w-0">
          <div className="text-base md:text-lg font-semibold leading-snug break-words" data-testid="welcome-title">{greeting()}, Sayın {data.name} hoşgeldiniz</div>
          <div className="text-xs text-slate-300 inline-flex items-center gap-1.5 mt-0.5" data-testid="welcome-last-login">
            <Clock className="w-3.5 h-3.5" />
            {data.prev_login_at ? <>Son girişiniz: <span className="font-mono text-slate-100">{fmtDateTime(data.prev_login_at)}</span></> : "Bu ilk girişiniz — hoş geldiniz!"}
          </div>
        </div>
      </div>
      <label className="inline-flex items-center gap-2 rounded-md border border-amber-400/50 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-100 md:ml-auto" title="Panodaki ciro, proje kartları ve yıllık özetler bu yıla göre hesaplanır" data-testid="welcome-year">
        <CalendarDays className="w-4 h-4 shrink-0" />
        <span>Yıl</span>
        <span className="font-mono text-base text-white" data-testid="welcome-year-value">{curYear}</span>
        {onYear && (
          <select value={String(curYear)} onChange={(e) => onYear(Number(e.target.value))} data-testid="welcome-year-select"
            className="bg-slate-800 text-white text-xs font-semibold border border-slate-600 rounded px-1.5 py-1 outline-none focus:border-amber-400">
            {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </label>
      <div className="flex flex-wrap gap-2" data-testid="welcome-summary">
        {data.pending_approvals > 0 && <Chip to="/onaylar" tone="amber" icon={ClipboardCheck} label={`${data.pending_approvals} kayıt onay bekliyor`} testid="welcome-pending-approvals" />}
        {inst?.due_count > 0 && <Chip to="/veri-girisi?bolum=kredi" tone="slate" icon={CalendarDays} label={`${inst.due_count} taksit 7 gün içinde`} value={fmtTL(inst.due_total)} testid="welcome-installments-due" />}
        {inst?.late_count > 0 && <Chip to="/veri-girisi?bolum=kredi" tone="red" icon={AlertTriangle} label={`${inst.late_count} geciken taksit`} value={fmtTL(inst.late_total)} testid="welcome-installments-late" />}
        {quiet && <span className="inline-flex items-center gap-2 rounded-md border border-emerald-400/40 bg-emerald-500/15 px-3 py-2 text-xs font-semibold text-emerald-200" data-testid="welcome-all-clear"><ClipboardCheck className="w-4 h-4" /> Bekleyen onay veya yaklaşan taksit yok</span>}
      </div>
    </div>
  );
}
